import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';

import { api } from '@/api';
import { PaginationParams } from '@/common/types';
import { API_URL } from '@/dtos';
import { LoginHistory, LoginHistoryPayload } from '@/modules/LoginHistory/loginHistory.types';
import { formatDateRange } from '@/utils/modifiers';

export interface EmployeeLoginHistoryPayload extends LoginHistoryPayload {}

interface LoginHistoryQueryParams extends Partial<PaginationParams> {
  employeeIds: Array<string>;
  startDate: Date | null;
  endDate: Date | null;
  enabled?: boolean;
}

const useGetEmployeeLoginHistoryQuery = ({
  employeeIds,
  startDate,
  endDate,
  page,
  perPage,
  enabled,
}: LoginHistoryQueryParams) => {
  const [from, to] = formatDateRange([startDate, endDate]);

  const employeeIdsKey = employeeIds?.join(',') || '';

  const { data, isLoading } = useQuery(
    // TODO: type is missing
    [API_URL.LOGIN_HISTORY_EMPLOYEE, employeeIdsKey, startDate, endDate, page, perPage],
    () =>
      api.get(API_URL.LOGIN_HISTORY_EMPLOYEE, {
        params: {
          employeeIds: employeeIds,
          start_date: from,
          end_date: to,
          page: page && page >= 0 ? page - 1 : 0,
          size: perPage || 50,
        },
      }),
    {
      enabled: !!employeeIds?.length && enabled,
    }
  );

  const employeeLoginHistory: LoginHistory[] = useMemo(() => {
    return (
      data?.data?.content?.map((loginHistory: EmployeeLoginHistoryPayload) => ({
        id: loginHistory?.id,
        username: loginHistory?.username,
        eventType: loginHistory?.event_type,
        statusType: loginHistory?.status_type,
        failureReasonType: loginHistory?.failure_reason_type,
        personalEmail: loginHistory?.personal_email,
        employeeIds: loginHistory?.employee_ids,
        contactId: loginHistory?.contact_id,
        createdAt: loginHistory?.created_at,
        sourceType: loginHistory?.source_type,
        ipAddress: loginHistory?.ip_address,
        geolocation: loginHistory?.geolocation,
        userAgent: loginHistory?.user_agent,
        mfaStatusType: loginHistory?.mfa_status_type,
        organizationId: loginHistory?.organization_id,
        organizationPath: loginHistory?.organization_path,
      })) || []
    );
  }, [data]);

  return {
    employeeLoginHistory,
    isLoading,
    total: data?.data?.totalElements || 0,
  };
};

export default useGetEmployeeLoginHistoryQuery;
